import {Button, ButtonProps, Text, Tooltip} from '@mantine/core';
import {modals} from '@mantine/modals';
import {AiOutlineReload} from 'react-icons/ai';

import {useReprovisionPipelineMutation, useStackStatusQuery} from '../hooks/usePipelinesQuery';
import {Pipeline} from '../types';


const REPROVISIONABLE_STATUSES = ['FAILED', 'ACTIVE'];

// FNP-1259: reprovisioning UPDATES an existing CloudFormation stack, so it
// cannot work when the stack is gone. The pipeline record does not know that —
// a stack deleted by hand in the console leaves the record reading ACTIVE — so
// the record's own status is not enough to gate this button on.
//
// The API reports an unfindable stack as DELETE_COMPLETE and flags it with
// `inferredFromAbsence`. Both signals are needed and they mean different
// things: an OBSERVED DELETE_COMPLETE is a stack that really is deleted, while
// an INFERRED one only means the API could not see it — which also covers a
// wrong account, a drifted stack name or an IAM denial. Either way reprovision
// cannot succeed, so both disable the button; only the wording differs.
const ABSENT_STACK_STATUS = 'DELETE_COMPLETE';

interface ReprovisionRecordButtonProps extends ButtonProps {
    pipeline: Pipeline;
}

export const ReprovisionRecordButton = ({pipeline, ...props}: ReprovisionRecordButtonProps) => {

    const reprovisionPipelineMutation = useReprovisionPipelineMutation();

    const {data: stackStatus, isLoading, isError} = useStackStatusQuery(pipeline.stackName);

    // React Query v5 reports `isLoading: true` for a query that is DISABLED, and
    // useStackStatusQuery is disabled when there is no stackName. Taken at face
    // value that would leave the button stuck on "Checking…" forever for a
    // record with no stack. Only treat it as in-flight when there is actually
    // something to fetch.
    const stackStatusLoading = !!pipeline.stackName && isLoading;

    const statusDisallows = !pipeline.pipelineStatus
        || !REPROVISIONABLE_STATUSES.includes(pipeline.pipelineStatus);

    // A record with no stackName has no stack to update, so there is nothing to
    // reprovision either.
    const stackMissing = !pipeline.stackName
        || stackStatus?.status === ABSENT_STACK_STATUS;

    // FAIL SAFE. If the stack check itself failed we do not know whether the
    // stack exists, and "unknown" must not be treated as "fine to reprovision".
    //
    // Found by the owner in the running app 2026-08-30: with the check erroring
    // for all five pipelines, every Reprovision button stayed enabled — the
    // gate only inspected `data.status`, which is undefined on error. That is
    // the original defect intact, just behind a check that was not consulted.
    const stackUnknown = !!pipeline.stackName && isError;

    const disabled = statusDisallows || stackMissing || stackUnknown || stackStatusLoading;

    const disabledReason = (): string | null => {
        if (stackStatusLoading) {
            return 'Checking whether the CloudFormation stack still exists…';
        }
        if (stackUnknown) {
            return 'Could not check whether this pipeline’s CloudFormation stack still exists, so reprovisioning is blocked. Reprovision fails outright when the stack is missing, so it is not offered until the check succeeds.';
        }
        if (!pipeline.stackName) {
            return 'This pipeline record has no CloudFormation stack, so there is nothing to reprovision. Use Provision to create one.';
        }
        if (stackStatus?.status === ABSENT_STACK_STATUS) {
            // Deliberately "could not be found", never "has been deleted".
            //
            // The API reports EVERY unfindable stack as DELETE_COMPLETE, and
            // absence has several causes besides deletion — a wrong account or
            // region, a drifted stack name, an IAM denial. `inferredFromAbsence`
            // is what separates an observed deletion from a failed lookup, but
            // it is NOT in the dev schema (added to source 2026-08-16, deployed
            // to test, dev is older), and AppSync rejects a query naming a field
            // its schema does not declare. Requesting it broke getStackStatus
            // for every pipeline in dev — found in the running app 2026-08-30.
            //
            // So the query stays portable and the wording stays cautious. The
            // advice is identical either way; only the claim about WHY differs,
            // and this is the half we can actually stand behind.
            return `The stack "${pipeline.stackName}" could not be found. Reprovision updates an existing stack and cannot recreate a missing one. If it was deleted, delete this pipeline record and use Provision instead.`;
        }
        if (statusDisallows) {
            return `A pipeline in status "${pipeline.pipelineStatus ?? 'unknown'}" cannot be reprovisioned.`;
        }
        return null;
    };

    const handleReprovision = async () => {
        await reprovisionPipelineMutation.mutateAsync(pipeline.id);
    };

    const openConfirmModal = () =>
        modals.openConfirmModal({
                                    title: 'Reprovision pipeline',
                                    children: (
                                        <Text size='sm'>
                                            This re-deploys the underlying CloudFormation stack for
                                            &quot;{pipeline.name ?? pipeline.id}&quot;. Are you sure you want to
                                            reprovision it?
                                        </Text>
                                    ),
                                    labels: {
                                        confirm: 'Reprovision',
                                        cancel: 'Cancel'
                                    },
                                    confirmProps: {color: 'red'},
                                    onConfirm: () => {
                                        void handleReprovision();
                                    },
                                });

    const reason = disabledReason();

    const button = (
        <Button
            disabled={disabled}
            onClick={openConfirmModal}
            loading={reprovisionPipelineMutation.isPending}
            leftSection={<AiOutlineReload/>}
            {...props}
        >
            Reprovision
        </Button>
    );

    // A disabled control with no stated reason is the defect this ticket is
    // about, one step earlier: the operator is left to guess. Mantine does not
    // fire pointer events on a disabled button, so the tooltip needs a wrapper
    // to have something to listen on.
    if (!reason) {
        return button;
    }

    return (
        <Tooltip label={reason} multiline w={320} withArrow position='bottom'>
            <span>
                {button}
                {/* The tooltip alone is not enough: it renders only on hover, so
                    a keyboard or screen-reader user gets a dead button with no
                    explanation. The reason is always in the accessibility tree
                    and announced when the disabled state is reached. */}
                <Text role='status' size='sm' c='dimmed' mt='xs'>
                    {reason}
                </Text>
            </span>
        </Tooltip>
    );

};
